import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Avatar,
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Business, ShoppingCart } from '@mui/icons-material';

import { Popup, SearchInput } from 'components';
import {
  AUTH_LAYOUT_LEFT_DRAWER_CLOSED_WIDTH,
  AUTH_LAYOUT_LEFT_DRAWER_OPENED_WIDTH,
  AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING,
  ROUTES,
} from 'consts';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { logout, selectCurrentUserInfo } from 'store/slices/auth.slice';
import { selectLayoutSlice } from 'store/slices/layout.slice';
import { useGetCountItemsInCartQuery } from '../store/apis/decal-order.api';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  fullWidth?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => !['open', 'fullWidth'].includes(prop as any),
})<AppBarProps>(({ theme, open, fullWidth }) => ({
  width: fullWidth
    ? '100%'
    : `calc(100% - ${AUTH_LAYOUT_LEFT_DRAWER_CLOSED_WIDTH + AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING + 24}px)`,
  marginLeft: `${AUTH_LAYOUT_LEFT_DRAWER_CLOSED_WIDTH + AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING}px`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${AUTH_LAYOUT_LEFT_DRAWER_OPENED_WIDTH + AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING + 24}px)`,
    marginLeft: `${AUTH_LAYOUT_LEFT_DRAWER_OPENED_WIDTH + AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface AuthHeaderDesktopLayoutProps {
  open: boolean;
  fullWidth?: boolean;
}

export const AuthHeaderDesktopLayout: React.FC<AuthHeaderDesktopLayoutProps> = (props) => {
  const dispatch = useAppDispatch();
  const { orgSlug, isAdmin, avatarUrl, orgLogoUrl } = useAppSelector(selectCurrentUserInfo);
  const { headerTitle = 'CleanCode' } = useAppSelector(selectLayoutSlice);
  const theme = useTheme();
  const navigate = useNavigate();
  const { open, fullWidth = false } = props;
  const [clicked, setClicked] = React.useState(false);

  const { data, isLoading: zonesCountLoading } = useGetCountItemsInCartQuery({ orgSlug });

  const handleClickOpen = () => {
    setClicked(!clicked);
  };

  const userMenuItems = [
    {
      id: 1,
      label: 'User Settings',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.PROFILE_USER_SETTINGS}`);
      },
      show: true,
    },
    {
      id: 2,
      label: 'Company Settings',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.COMPANY_SETTINGS}`);
      },
      show: isAdmin,
    },
    {
      id: 3,
      label: 'Zone Categories',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.ZONE_CATEGORIES}`);
      },
      show: isAdmin,
    },
    {
      id: 4,
      label: 'Notifications',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.PROFILE_NOTIFICATIONS}`);
      },
      show: true,
    },
    {
      id: 5,
      label: 'Password',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.PROFILE_PASSWORD}`);
      },
      show: true,
    },
    {
      id: 6,
      label: 'Invite Users',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.PROFILE_INVITE_USERS}`);
      },
      show: isAdmin,
    },
    {
      id: 7,
      label: 'Order Decals',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.ORDER_DECALS}`);
      },
      show: isAdmin,
    },
    {
      id: 8,
      label: 'Billing',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.PROFILE_BILLING}`);
      },
      show: isAdmin,
    },
    {
      id: 9,
      label: 'Cart',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.REVIEW_CART}`);
      },
      show: isAdmin,
    },
    {
      id: 10,
      label: 'Log Out',
      onClick: () => {
        dispatch(logout());
      },
      show: true,
    },
  ];

  return (
    <AppBar
      position="fixed"
      open={open}
      fullWidth={fullWidth}
      onChange={handleClickOpen}
      color="transparent"
      sx={{
        backgroundColor: 'white',
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: '0px 0px 4px rgb(0 0 0 / 12%)',
        borderRadius: 1,
      }}
    >
      <Toolbar>
        <Typography variant="h6">{headerTitle}</Typography>

        <Box marginLeft="auto" width={380}>
          <SearchInput />
        </Box>

        {isAdmin && (
          <Box marginLeft={2}>
            <ButtonBase
              onClick={() => {
                navigate(ROUTES.REVIEW_CART);
              }}
              sx={{
                color: theme.palette.text.secondary,
                width: 40,
                height: 40,
                border: `1px solid ${theme.palette.text.secondary}A0`,
                borderRadius: 1,
              }}
            >
              <Box>
                <Badge
                  badgeContent={
                    <Box
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderTopLeftRadius: 14,
                        borderBottomLeftRadius: 14,
                        borderTopRightRadius: 14,
                        borderBottomRightRadius: 14,
                        paddingX: 0.5,
                        paddingY: 0.3,
                        fontSize: 10,
                        fontWeight: 600,
                        border: `1px solid ${theme.palette.primary.contrastText}`,
                        minWidth: 16,
                      }}
                    >
                      {zonesCountLoading ? <CircularProgress color="inherit" size={8} /> : data?.count}
                    </Box>
                  }
                >
                  <ShoppingCart
                    sx={{
                      fontSize: 20,
                    }}
                  />
                </Badge>
              </Box>
            </ButtonBase>
          </Box>
        )}

        <Box
          sx={{
            marginLeft: 2,
          }}
        >
          <Popup items={userMenuItems.filter((n) => n.show)} onClose={() => setClicked(false)} width={220}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <SmallAvatar
                  alt="Current User"
                  src={avatarUrl}
                  sx={{
                    cursor: 'pointer',
                  }}
                />
              }
            >
              <ButtonBase
                sx={{
                  borderRadius: '100%',
                }}
              >
                <Avatar
                  alt="Current Company"
                  src={orgLogoUrl}
                  sx={{
                    width: 48,
                    height: 48,
                    boxShadow: 2,
                  }}
                >
                  {
                    <Business
                      sx={{
                        fontSize: 32,
                      }}
                    />
                  }
                </Avatar>
              </ButtonBase>
            </Badge>
          </Popup>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 30,
  height: 30,
  marginBottom: 10,
  marginRight: -10,
  border: `2px solid ${theme.palette.background.paper}`,
  backgroundColor: theme.palette.grey[400],
}));
